import {
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { LocalAudioTrack, createLocalAudioTrack } from 'twilio-video';
import { VideoCallContext, VideoGuestContext } from '../../../../core';
import AudioLevelIndicator from '../../../AudioLevelIndicator/AudioLevelIndicator';

interface IAudioInputProp {
  selectedAudioTrack?: LocalAudioTrack;
  setSelectedAudioTrack: Dispatch<SetStateAction<LocalAudioTrack | undefined>>;
  setAudioInputDeviceChangeStatus: Dispatch<SetStateAction<boolean>>;
  isGuest: boolean;
}
function AudioInputList({
  selectedAudioTrack,
  setSelectedAudioTrack,
  setAudioInputDeviceChangeStatus,
  isGuest,
}: IAudioInputProp) {

  const guestContext = useContext(VideoGuestContext);
  const callContext = useContext(VideoCallContext);

  const room = isGuest ? guestContext?.room : callContext?.room;
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();
  const [audioInputDevice, setAudioInputDevice] = useState<MediaDeviceInfo[]>(
    []
  );
  const participantAudioTrack = Array.from(
    room?.localParticipant.audioTracks.values()!
  )?.[0]?.track;

  const currentDeviceId = participantAudioTrack?.mediaStreamTrack.getSettings().deviceId;
  const selectAudioInputDevice: MediaDeviceInfo | undefined =
    audioInputDevice.find((device) => device.deviceId === currentDeviceId);

  const replaceTrack = async (newDeviceId: string) => {
    await selectedAudioTrack?.restart({ deviceId: { exact: newDeviceId } });
    setSelectedDeviceId(newDeviceId);
    setAudioInputDeviceChangeStatus(true);
  };

  useEffect(() => {
    if (audioInputDevice.length > 0 && !selectedDeviceId) {
      setSelectedDeviceId(audioInputDevice[0].deviceId);
    }
  }, [audioInputDevice, selectedDeviceId]);


  useEffect(() => {

    let initialAudioTrack: LocalAudioTrack;
    let audioInputDevices: MediaDeviceInfo[];

    const getAudioTrack = async () => {

      initialAudioTrack = await createLocalAudioTrack();
      const currentDevice = audioInputDevices?.find(
        (device) =>
          device.label === participantAudioTrack?.mediaStreamTrack?.label
      );
      await initialAudioTrack.restart({
        deviceId: currentDevice?.deviceId,
      });
      setSelectedAudioTrack(initialAudioTrack);
      setSelectedDeviceId(currentDevice?.deviceId);
    };

    const getAudioInputDevices = async () => {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
      audioInputDevices = mediaDevices.filter(
        (mediaDevice) => mediaDevice.kind === 'audioinput'
      );
      getAudioTrack();
      setAudioInputDevice(audioInputDevices);
    };

    getAudioInputDevices();

    return () => {
      if (!initialAudioTrack) {
        return;
      }

      room!.localParticipant.audioTracks.forEach((track) => {
        if (
          track.track.mediaStreamTrack.label ===
          initialAudioTrack.mediaStreamTrack.label
        ) {
          initialAudioTrack.stop();
        }
      });
    };
  }, [participantAudioTrack, room, setSelectedAudioTrack]);

  return (
    <div>
      <Typography variant="subtitle2" gutterBottom>
        Audio Input
      </Typography>
      <Grid container alignItems="center" justifyContent="space-between">
        <div className="inputSelect">
          {audioInputDevice.length ? (
            <FormControl fullWidth>
              <Select
                value={selectedDeviceId ?? ''}
                onChange={(e) => {
                  const newDeviceId = e.target.value as string;
                  // console.log('Selected deviceId:', newDeviceId);
                  replaceTrack(newDeviceId);
                }}
                variant="outlined"
                MenuProps={{
                  PaperProps: { className: 'device-selection-menu' },
                }}
              >
                {audioInputDevice.map((device) => (
                  <MenuItem value={device.deviceId} key={device.deviceId}>
                    {device.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography>
              {selectedAudioTrack?.mediaStreamTrack.label || 'No Local Audio'}
            </Typography>
          )}
        </div>
        <span className="audio-level-indicator">
          <AudioLevelIndicator audioTrack={selectedAudioTrack} color="currentColor" />
        </span>
      </Grid>
    </div>
  );
}

export default AudioInputList;
